import { useShoppingcartStore } from "@features/cart/shopping-cart-store";
import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { useAuth } from "./context-auth";
import type { Product } from "@type/product";
import type { AllergyType, OriginalCategoryType, ProductType } from "@type/global";
import type { Statiegeld } from "@type/statiegeld";
import type { DeliveryArea, MandateProvider } from "@provider/app-data-queries";
import {
  categoriesQuery,
  deliveryAreaQuery,
  issuersQuery,
  mandateProviderQuery,
  prefetchCategories,
  prefetchDeliveryArea,
  prefetchMandateProvider,
  prefetchProducts,
  productsQuery,
  statiegeldQuery,
} from "@provider/app-data-queries";
import { useAppState } from "@utils";
import { allowedZipcodes } from "../data";
import { useStockRefresh } from "@hooks/use-stock-refresh";
import { setProductImageVersions } from "@provider/image-url";

// Losse contexts per soort data: een voorraad-update of een statiegeld-refetch
// mag niet elke ProductItem, elk categoriemenu en elke postcodecheck
// opnieuw laten renderen.
export interface QuerySlice<T> {
  data: T;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

interface ProductsSlice extends QuerySlice<ProductType[]> {
  byId: Map<number, ProductType>;
}

const ProductsContext = React.createContext<ProductsSlice>(undefined as any);
const CategoriesContext = React.createContext<QuerySlice<OriginalCategoryType[]>>(undefined as any);
const StatiegeldContext = React.createContext<QuerySlice<Statiegeld[] | undefined>>(undefined as any);
const DeliveryAreaContext = React.createContext<DeliveryArea>(allowedZipcodes);
// Waar de klant machtigt; zolang het niet bekend is (of bij een fout) de
// bestaande eMandates-UI.
const MandateProviderContext = React.createContext<MandateProvider>("emandates");

export const AppDataProvider = ({ children }: { children: React.ReactNode }) => {
  const { products, categories, statiegeld, deliveryArea, mandateProvider } = useAppData();

  return (
    <ProductsContext.Provider value={products}>
      <CategoriesContext.Provider value={categories}>
        <StatiegeldContext.Provider value={statiegeld}>
          <DeliveryAreaContext.Provider value={deliveryArea}>
            <MandateProviderContext.Provider value={mandateProvider}>{children}</MandateProviderContext.Provider>
          </DeliveryAreaContext.Provider>
        </StatiegeldContext.Provider>
      </CategoriesContext.Provider>
    </ProductsContext.Provider>
  );
};

// prefetch queries before first render start
prefetchCategories();
prefetchProducts();
prefetchDeliveryArea();
prefetchMandateProvider();

const NO_CATEGORIES: OriginalCategoryType[] = [];

function useAppData() {
  const { token } = useAuth();
  const load = useShoppingcartStore((state) => state.load);

  const appState = useAppState();
  const enabled = appState === "active";
  React.useEffect(() => {
    if (token && enabled) {
      load();
    }
  }, [token, enabled]);

  // Verversen bij terugkeren naar de app doet react-query zelf
  // (refetchOnWindowFocus + staleTime, zie app-data-queries.tsx).
  const categories = useQuery({ ...categoriesQuery });
  const products = useQuery({ ...productsQuery });
  const deliveryArea = useQuery({ ...deliveryAreaQuery });
  const mandateProvider = useQuery({ ...mandateProviderQuery });
  const statiegeld = useQuery({ ...statiegeldQuery, enabled: !!token });

  const productData = React.useMemo(() => {
    // pid -> image_version/variants_version voor de plekken die alleen een pid kennen
    setProductImageVersions(products.data);
    return simplifyProducts(products.data || []);
  }, [products.data]);
  const productsById = React.useMemo(() => new Map(productData.map((p) => [p.id, p])), [productData]);
  useStockRefresh(!!token, products.data, productsQuery.queryKey);

  const productSlice = useQuerySlice(products, productData);
  const productsValue = React.useMemo(() => ({ ...productSlice, byId: productsById }), [productSlice, productsById]);
  const deliveryAreaData = deliveryArea.data;

  return {
    products: productsValue,
    categories: useQuerySlice(categories, categories.data || NO_CATEGORIES),
    statiegeld: useQuerySlice(statiegeld, statiegeld.data),
    deliveryArea: React.useMemo(
      () => (deliveryAreaData?.nl?.length ? deliveryAreaData : allowedZipcodes),
      [deliveryAreaData],
    ),
    mandateProvider: mandateProvider.data ?? "emandates",
  };
}

// Alleen de velden die schermen gebruiken, gememoïseerd: zo verandert de
// context niet bij elke isFetching-wissel van een achtergrond-refetch.
function useQuerySlice<T>(query: UseQueryResult<unknown>, data: T): QuerySlice<T> {
  const { isLoading, isError, error, refetch } = query;
  return React.useMemo(
    () => ({ data, isLoading, isError, error, refetch }),
    [data, isLoading, isError, error, refetch],
  );
}

export function useCategories() {
  return React.useContext(CategoriesContext);
}

// Bezorggebied: van de server, met de ingebouwde lijst als terugval.
export function useDeliveryArea() {
  return React.useContext(DeliveryAreaContext);
}

export function useMandateProvider() {
  return React.useContext(MandateProviderContext);
}

export function useStatiegeld() {
  return React.useContext(StatiegeldContext);
}

// Per product-object onthouden: na een voorraad-update of een 304 blijven de
// ongewijzigde producten hetzelfde object, zodat React.memo ze overslaat.
const simplifiedProducts = new WeakMap<Product, ProductType>();

export function simplifyProducts(data: undefined | Product[]): ProductType[] {
  return data?.filter((it) => !!it).map(simplifyProductCached) || [];
}

function simplifyProductCached(product: Product): ProductType {
  let simplified = simplifiedProducts.get(product);
  if (!simplified) {
    simplified = simplifyProductData(product);
    simplifiedProducts.set(product, simplified);
  }
  return simplified;
}

function simplifyProductData(product: Product): ProductType {
  const voedingswaardenSimpeler = product.voedingswaarden?.split("$").map((v) => {
    const [label, value] = v.split("#");
    return { label: label || "", value: value || "" };
  });

  return {
    ...product,
    voedingswaardenSimpeler,
    allergieSimpeler: allergies.filter((a, i) => {
      const mask = Math.pow(2, i);
      return !!(product.allergiecode & mask);

      // return !!(product?.allergiecode && mask);
    }),
  };
}

export function simplifyProduct(product: Product): ProductType {
  const voedingswaardenSimpeler = product.voedingswaarden?.split("$").map((v) => {
    const [label, value] = v.split("#");
    return { label: label || "", value: value || "" };
  });

  return {
    ...product,
    voedingswaardenSimpeler,
    allergieSimpeler: allergies.filter((a, i) => {
      const mask = Math.pow(2, i - 1);
      return !!(product?.allergiecode && mask);
    }),
  };
}

// Alleen nodig op de account- en betaalschermen: daar pas ophalen.
export function useIssuersList() {
  return useQuery({ ...issuersQuery, refetchOnWindowFocus: true });
}

export function useProducts() {
  return React.useContext(ProductsContext);
}

export function useProduct(id: number) {
  const productQuery = useProducts();
  return {
    ...productQuery,
    data: productQuery.byId.get(id),
  };
}

export function useDiscountProducts() {
  const productQuery = useProducts();
  const data = React.useMemo(
    () => productQuery.data.filter((p) => p.actieprijs > 0 && p.actie > 0),
    [productQuery.data],
  );
  return { ...productQuery, data };
}

export const allergies: AllergyType[] = [
  { id: 1, name: "Eivrij", icon: "eivrij.png" },
  { id: 2, name: "Schaaldiervrij", icon: "schaaldiervrij.png" },
  { id: 3, name: "Lupinevrij", icon: "lupinevrij.png" },
  { id: 4, name: "Selderijvrij", icon: "selderijvrij.png" },
  {
    id: 5,
    name: "Melk- en lactosevrij",
    icon: "melk-en-lactosevrij.png",
  },
  { id: 6, name: "Sesamvrij", icon: "sesamvrij.png" },
  { id: 7, name: "Mosterdvrij", icon: "mosterdvrij.png" },
  {
    id: 8,
    name: "Pinda- en notenvrij",
    icon: "pinda-en-notenvrij.png",
  },
  { id: 9, name: "Sojavrij", icon: "sojavrij.png" },
  { id: 10, name: "Visvrij", icon: "visvrij.png" },
  { id: 11, name: "Sulfietvrij", icon: "sulfietvrij.png" },
  { id: 12, name: "Weekdiervrij", icon: "weekdiervrij.png" },
  {
    id: 13,
    name: "Tarwe- en glutenvrij",
    icon: "tarwe-en-glutenvrij.png",
  },
  { id: 14, name: "Vegetarisch", icon: "vegetarisch.png" },
  { id: 15, name: "Veganistisch", icon: "veganistisch.png" },
];

export function useIngredientsList({ ingredientProductPids }: { ingredientProductPids: number[] }) {
  const { data: products } = useProducts();

  return products.filter((prod) => {
    if (ingredientProductPids.includes(prod.pid)) {
      return prod;
    }
  });
}
